import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Wheat, MapPin, AlertTriangle, BookOpen } from "lucide-react";
import { api, fmtPrice } from "@/lib/api";

export default function QuickView() {
  const { sku } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/products/${sku}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [sku]);

  if (notFound) return <div className="flex min-h-screen items-center justify-center text-ink/50">لم يُعثر على المنتج.</div>;
  if (!p) return <div className="flex min-h-screen items-center justify-center text-ink/50">جارِ التحميل...</div>;

  const Section = ({ title, children }) => (
    <div className="border-t border-olive/10 py-4">
      <h3 className="mb-2 font-display text-sm font-semibold text-olive">{title}</h3>
      {children}
    </div>
  );

  return (
    <div className="min-h-screen bg-ivory">
      <div className="mx-auto max-w-md px-5 py-8">
        <div className="mb-5 flex items-center justify-center gap-2 text-olive-dark">
          <Wheat className="h-6 w-6" />
          <span className="font-display text-lg font-bold">سهول جنين</span>
        </div>

        <div className="overflow-hidden rounded-3xl border border-olive/10 bg-white">
          <img src={p.images?.main} alt={p.name_ar} className="aspect-square w-full object-cover" />
          <div className="p-5">
            <h1 className="font-display text-2xl font-bold text-ink">{p.name_ar}</h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-ink/55">
              <span className="font-display text-lg font-bold text-olive-dark">{fmtPrice(p.price)}</span>
              <span>· رقم المنتج {p.sku}</span>
            </div>

            <Section title="المصدر الجغرافي">
              <p className="flex items-center gap-1.5 text-sm text-ink/70"><MapPin className="h-4 w-4 text-olive" /> {p.geo_source_ar}</p>
              {p.origin_text && <p className="mt-1 text-sm text-ink/60">{p.origin_text}</p>}
            </Section>

            {p.ingredients?.length > 0 && (
              <Section title="المكوّنات">
                <p className="text-sm text-ink/70">{p.ingredients.map((i) => i.name_ar).join("، ")}</p>
              </Section>
            )}

            {p.allergens?.length > 0 && (
              <Section title="مسببات الحساسية">
                <p className="flex items-center gap-1.5 text-sm text-palred"><AlertTriangle className="h-4 w-4" /> {p.allergens.join("، ")}</p>
              </Section>
            )}

            {Object.keys(p.nutrition || {}).length > 0 && (
              <Section title="القيمة الغذائية">
                <div className="flex flex-wrap gap-3 text-sm text-ink/70">
                  {p.nutrition.serving && <span>الحصة: {p.nutrition.serving}</span>}
                  {p.nutrition.calories != null && <span>سعرات: {p.nutrition.calories}</span>}
                  {p.nutrition.fat_g != null && <span>دهون: {p.nutrition.fat_g}غ</span>}
                  {p.nutrition.carbs_g != null && <span>كربوهيدرات: {p.nutrition.carbs_g}غ</span>}
                </div>
              </Section>
            )}

            {p.usage?.length > 0 && (
              <Section title="كيف يُستخدم؟">
                <ul className="space-y-1 text-sm text-ink/70">
                  {p.usage.map((u, i) => <li key={i}>• {u.context}: {u.text}</li>)}
                </ul>
              </Section>
            )}

            {p.short_story && <Section title="حكاية المنتج"><p className="text-sm leading-relaxed text-ink/70">{p.short_story}</p></Section>}

            {p.heritage?.approved && (
              <Section title="من التراث الإسلامي">
                <div className="rounded-xl bg-gold/[0.06] p-3">
                  {p.heritage.quote && <p className="mb-1 font-display text-olive-dark">{p.heritage.quote}</p>}
                  <p className="text-sm text-ink/70">{p.heritage.text}</p>
                  <p className="mt-1 text-xs text-ink/50">{p.heritage.source} — {p.heritage.reference}</p>
                </div>
              </Section>
            )}
          </div>
        </div>

        <p className="mt-4 text-center text-xs text-ink/45">هذه الصفحة مرتبطة بالمنتج الأصلي في سهول جنين.</p>
        <Link to={`/product/${p.slug}`} className="mt-4 block rounded-full bg-olive py-3 text-center font-semibold text-ivory">عرض المنتج كاملاً</Link>
      </div>
    </div>
  );
}
